import React, { memo } from "react";

import { FlashList, ListRenderItem } from "@/components/atoms/FlashList";
import ItemSeparator from "@/components/molecules/ItemSeparator";
import { Stack } from "@/components/atoms/Stack";
import { PlayerReportRow } from "@/features/reports/utils/mergePlayerReportRows";

/**
 * Props for the MainView component
 */
interface Props {
  /**
   * The merged report rows to display — single-player scout reports and
   * match-report fixtures in one list, newest first (SE-7856).
   */
  readonly items: PlayerReportRow[];
  /**
   * Function to render each individual row. The row's `kind` decides which
   * component it becomes; this view stays agnostic.
   */
  readonly renderItem: ListRenderItem<PlayerReportRow>;
  /**
   * Function to extract a unique key for each item in the list
   */
  readonly keyExtractor: (item: PlayerReportRow, index: number) => string;
  /**
   * The match-reports section (SE-6986), rendered ABOVE the scout reports.
   *
   * A list header rather than a sibling, so the two sections share one scroll
   * — a nested list inside a scroll view would either clip or double-scroll.
   */
  readonly listHeader?: React.ReactElement;
}

/**
 * Displays a scrollable list of scout reports for a player.
 * Renders report items in a flat list with separators.
 * @param root0 - The component props
 * @param root0.renderItem - Function to render each individual scout report item in the list
 * @param root0.items - Array of scout report data to display in the list
 * @param root0.keyExtractor - Function to extract a unique key for each item in the list
 * @param root0.listHeader - The match-reports section, rendered above the list
 */
/**
 * Tells FlashList which rows may be recycled into one another.
 *
 * A scout row and a match row have different subtrees, so without this the
 * list recycles one into the other and pays a full re-layout on every reuse.
 * @param row - The row about to be rendered.
 * @returns The row's kind, used as the recycling pool key.
 */
const getItemType = (row: PlayerReportRow): string => row.kind;

/**
 * Displays a scrollable list of a player's reports.
 *
 * Renders both kinds — single-player scout reports and match-report fixtures —
 * in one flat list with separators. Which component a row becomes is the
 * caller's business; this view only knows that rows of different kinds must
 * not be recycled into one another.
 * @param root0 - The component props
 * @param root0.renderItem - Function to render each individual row
 * @param root0.items - The merged rows to display
 * @param root0.keyExtractor - Function to extract a unique key for each row
 * @param root0.listHeader - The match-reports section, rendered above the list
 * @returns The list element.
 */
const MainView = ({ renderItem, items, keyExtractor, listHeader }: Props) => (
  <Stack className="flex-1 pb-4">
    <FlashList
      data={items}
      renderItem={renderItem}
      getItemType={getItemType}
      ListHeaderComponent={listHeader}
      contentContainerStyle={{ paddingHorizontal: 16, paddingVertical: 4 }}
      scrollEventThrottle={16}
      keyExtractor={keyExtractor}
      ItemSeparatorComponent={ItemSeparator}
    />
  </Stack>
);

MainView.displayName = "MainView";

export default memo(MainView);
